import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import { 
  ArrowRight, ArrowUpRight, CheckCircle2, Phone, Mail, MapPin, Clock, Send,
  Award, Users, Shield, Zap, Globe, Building2, Play,
  Layers, LayoutGrid, DoorOpen, SlidersHorizontal, Fence, Wrench
} from "lucide-react";
import Counter from "@/components/shared/Counter";
import { SERVICES, PROJECTS, STATS, WHY_CHOOSE, CONTACT_INFO, COMPANY_INFO, LOGO_URL } from "@/data/siteData";

const iconMap = {
  Layers, LayoutGrid, DoorOpen, SlidersHorizontal, Fence, Wrench,
  Award, Users, Shield, Zap, Globe, Building2
};

// Modern Hero Section with Parallax
const HeroSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const titleWords = ["Great", "International"];
  const subtitleWords = ["Glass", "&", "Aluminium"];

  return (
    <section ref={ref} className="hero-modern" data-testid="hero-section">
      {/* Background with Parallax */}
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src="/images/Frameless Glass Façade at City Walk,  Al Safa St - Al Wasl - Dubai.webp"
          alt="Modern Architecture"
          className="w-full h-full object-cover scale-105"
        />
        <div className="hero-overlay bg-gradient-to-br from-slate-900/95 via-slate-900/80 to-[#0056D2]/40" />
      </motion.div>

      {/* Content */}
      <motion.div style={{ opacity }} className="relative z-10 max-w-[1400px] mx-auto px-6 py-32">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="section-label text-[#D4AF37] mb-8 block font-bold text-sm">
              Premier Fabrication Services
            </span>
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="show"
            variants={{
              hidden: { opacity: 0 },
              show: { opacity: 1, transition: { staggerChildren: 0.1 } }
            }}
            className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-[0.95] mb-8 drop-shadow-2xl"
          >
            <div className="flex gap-4 flex-wrap">
              {titleWords.map((word, i) => (
                <motion.span key={i} variants={{ hidden: { opacity: 0, y: 40 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } } }}>
                  {word}
                </motion.span>
              ))}
            </div>
            <div className="flex gap-4 flex-wrap text-gradient-gold mt-2">
              {subtitleWords.map((word, i) => (
                <motion.span key={i} variants={{ hidden: { opacity: 0, y: 40 }, show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } } }}>
                  {word}
                </motion.span>
              ))}
            </div>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl md:text-2xl text-white/80 font-medium leading-relaxed mb-12 max-w-2xl drop-shadow-md"
          >
            {COMPANY_INFO.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link to="/contact" data-testid="hero-cta-quote" className="btn-modern btn-gold animated-gradient-border shadow-2xl shadow-[#D4AF37]/20">
                Get a Quote <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link to="/projects/gallery" data-testid="hero-cta-projects" className="btn-modern btn-outline-gold !border-white !text-white hover:!bg-white hover:!text-slate-900 premium-glass">
                View Our Work <ArrowUpRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Floating Stats Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, rotate: -5 }}
          animate={{ opacity: 1, scale: 1, rotate: 0 }}
          transition={{ duration: 1, delay: 0.8, type: "spring" }}
          whileHover={{ scale: 1.05, rotate: 2 }}
          className="hidden lg:block stats-badge premium-glass animated-gradient-border shadow-2xl"
        >
          <div className="flex items-center gap-8">
            <div className="text-center">
              <span className="block text-4xl font-bold text-[#D4AF37] drop-shadow-md">15+</span>
              <span className="text-sm text-slate-800 uppercase tracking-wider font-bold">Years</span>
            </div>
            <div className="w-px h-12 bg-slate-300" />
            <div className="text-center">
              <span className="block text-4xl font-bold text-[#D4AF37] drop-shadow-md">500+</span>
              <span className="text-sm text-slate-800 uppercase tracking-wider font-bold">Projects</span>
            </div>
          </div>
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="flex flex-col items-center gap-2"
        >
          <span className="text-xs text-white/70 font-bold uppercase tracking-[0.2em]">Scroll</span>
          <div className="w-px h-16 bg-gradient-to-b from-[#D4AF37] to-transparent" />
        </motion.div>
      </motion.div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section ref={ref} className="py-24 md:py-32 bg-transparent relative overflow-hidden" data-testid="about-section">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="relative"
            whileHover={{ rotateY: 5, rotateX: 5 }}
            style={{ perspective: 1000 }}
          >
            <motion.div className="relative img-zoom overflow-hidden rounded-xl shadow-2xl" style={{ y: imgY }}>
              <img
                src="/images/Custom Interior Fit-Out with Glass & Stone Finishes at Madinat Jumeirah, Al Sufouh 1 - Dubai.webp"
                alt="Glass Installation"
                className="w-full aspect-[4/5] object-cover"
              />
            </motion.div>
            {/* Floating accent */}
            <motion.div 
              animate={{ y: [0, -20, 0] }} 
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-8 -right-8 w-2/3 aspect-video bg-gradient-to-br from-[#D4AF37]/30 to-[#0056D2]/20 blur-3xl -z-10 rounded-full" 
            />
            <div className="absolute -top-8 -left-8 w-24 h-24 border-2 border-[#D4AF37]/30 -z-10" />
            
            {/* Experience badge */}
            <motion.div 
              whileHover={{ scale: 1.1, rotate: -5 }}
              className="absolute bottom-8 right-8 premium-glass animated-gradient-border p-6 shadow-2xl"
            >
              <span className="block text-4xl font-bold text-slate-900 drop-shadow-sm">15+</span>
              <span className="text-sm text-slate-800 uppercase tracking-wider font-bold">Years Experience</span>
            </motion.div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span className="section-label mb-6 block font-bold">About Us</span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] mb-8">
              Enhancing Modern<br />
              <span className="text-gradient-gold drop-shadow-sm">Architecture</span>
            </h2>
            <div className="gradient-line mb-8" />
            
            <p className="text-lg text-slate-700 font-medium leading-relaxed mb-6">
              Great International Glass & Aluminium Works LLC specializes in providing
              high-quality architectural glass and aluminium solutions tailored to meet
              the diverse needs of residential, commercial, and industrial projects.
            </p>
            
            <p className="text-slate-600 leading-relaxed mb-10">
              With a commitment to innovation, precision, and aesthetic excellence,
              we have built a strong reputation for delivering durable, functional,
              and visually striking designs that enhance modern architecture.
            </p>

            <div className="grid grid-cols-2 gap-4 mb-10">
              {["High-Quality Glass", "Commercial Solutions", "Residential Projects", "Durable Designs"].map((item, i) => (
                <motion.div
                  key={item}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.4 + i * 0.1 }}
                  whileHover={{ x: 10, color: "#D4AF37" }}
                  className="flex items-center gap-3 cursor-pointer group"
                >
                  <div className="w-8 h-8 bg-[#D4AF37]/10 flex items-center justify-center rounded-full group-hover:bg-[#D4AF37] transition-colors duration-300">
                    <CheckCircle2 className="w-4 h-4 text-[#D4AF37] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <span className="text-slate-700 text-sm font-bold transition-colors">{item}</span>
                </motion.div>
              ))}
            </div>

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="inline-block">
              <Link to="/about/company-profile" className="btn-modern btn-dark premium-glass-dark shadow-xl hover:shadow-[#D4AF37]/20">
                Learn More <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Bento Grid Services Section
const ServicesSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const bentoClasses = [
    "md:col-span-8", // Large
    "md:col-span-4", // Small
    "md:col-span-4", // Small
    "md:col-span-4", // Small
    "md:col-span-4", // Small
    "md:col-span-8", // Large
  ];

  return (
    <section ref={ref} className="py-24 md:py-32 bg-transparent relative" data-testid="services-section">
      {/* Dynamic background glow */}
      <motion.div 
        animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] h-[500px] bg-gradient-to-r from-[#D4AF37]/10 to-[#0056D2]/10 blur-[100px] -z-10 rounded-full pointer-events-none"
      />

      <div className="max-w-[1400px] mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="max-w-2xl mb-16"
        >
          <span className="section-label mb-6 block font-bold">Our Services</span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1] mb-6">
            Specialized Glass &<br />
            <span className="text-[#0056D2] drop-shadow-sm">Aluminium Works</span>
          </h2>
          <div className="gradient-line" />
        </motion.div>

        {/* Bento Grid */}
        <div className="bento-grid">
          {SERVICES.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            const isLarge = index === 0 || index === 5;
            
            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, scale: 0.9, y: 50 }}
                animate={isInView ? { opacity: 1, scale: 1, y: 0 } : {}}
                transition={{ delay: index * 0.1, type: "spring", stiffness: 100, damping: 20 }}
                whileHover={{ y: -10, scale: 1.02 }}
                className={`${bentoClasses[index]} col-span-1`}
              >
                <Link
                  to={`/services/${service.slug}`}
                  className={`relative overflow-hidden h-full flex group rounded-2xl bg-white border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_30px_60px_-15px_rgba(212,175,55,0.2)] transition-all duration-500 p-8 ${isLarge ? 'flex-col md:flex-row md:items-center gap-8' : 'flex-col'}`}
                  data-testid={`service-card-${service.id}`}
                >
                  {/* Premium Gold Hover Line (Left Edge) */}
                  <div className="absolute top-0 left-0 w-1.5 h-full bg-gradient-to-b from-[#D4AF37] to-[#C9A032] scale-y-0 group-hover:scale-y-100 origin-top transition-transform duration-500 ease-out z-20" />

                  {/* Subtle Hover Glow Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/[0.03] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none z-0" />
                  
                  {/* Decorative corner accent */}
                  <div className="absolute -bottom-12 -right-12 w-32 h-32 bg-gradient-to-br from-[#D4AF37]/10 to-transparent blur-2xl rounded-full group-hover:scale-150 transition-transform duration-700 pointer-events-none z-0" />

                  {/* Icon */}
                  <div className={`icon-container ${isLarge ? 'w-24 h-24' : 'mb-8 w-16 h-16'} rounded-2xl bg-gradient-to-br from-[#D4AF37]/5 to-transparent border border-[#D4AF37]/20 shadow-[0_4px_20px_-4px_rgba(212,175,55,0.1)] flex items-center justify-center flex-shrink-0 group-hover:bg-gradient-to-br group-hover:from-[#D4AF37] group-hover:to-[#C9A032] group-hover:shadow-[0_10px_30px_-10px_rgba(212,175,55,0.5)] transition-all duration-500 relative z-10`}>
                    {IconComponent && <IconComponent className={`${isLarge ? 'w-10 h-10' : 'w-8 h-8'} group-hover:scale-110 group-hover:text-white transition-all duration-500 text-[#D4AF37]`} strokeWidth={1.5} />}
                  </div>
                  
                  {/* Text Content */}
                  <div className="flex-1 relative z-10">
                    <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-3 group-hover:text-[#D4AF37] transition-colors duration-300 tracking-tight">
                      {service.title}
                    </h3>
                    <p className={`text-slate-500 font-medium leading-relaxed ${isLarge ? 'text-lg' : 'text-sm line-clamp-3'}`}>
                      {service.description}
                    </p>
                    
                    {isLarge && (
                      <div className="mt-8 flex items-center gap-2 text-[#D4AF37] font-bold text-sm uppercase tracking-widest group-hover:pl-4 transition-all duration-500">
                        Explore Service <ArrowRight className="w-5 h-5 group-hover:translate-x-2 transition-transform duration-500" />
                      </div>
                    )}
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8 }}
          className="text-center mt-16"
        >
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="inline-block">
            <Link to="/services" className="btn-modern btn-outline-gold premium-glass border-2 shadow-xl hover:shadow-[#D4AF37]/20">
              View All Services <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// Why Choose Section
const WhyChooseSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-24 md:py-32 bg-transparent relative" data-testid="why-choose-section">
      <div className="max-w-[1400px] mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="section-label justify-center mb-6 font-bold">Why Choose Us</span>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            Why Leading Architects<br />Choose Us
          </h2>
          <div className="gradient-line mx-auto" />
        </motion.div>

        <motion.div 
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
          variants={{
            hidden: { opacity: 0 },
            show: {
              opacity: 1,
              transition: { staggerChildren: 0.1 }
            }
          }}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6"
        >
          {WHY_CHOOSE.map((item, index) => {
            const IconComponent = iconMap[item.icon];
            return (
              <motion.div
                key={item.title}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } }
                }}
                whileHover={{ y: -10, scale: 1.05 }}
                className="group text-center p-6 premium-glass hover:border-[#D4AF37]/50 hover:shadow-2xl transition-all duration-300"
                data-testid={`why-choose-${index}`}
              >
                <div className="icon-container mx-auto mb-4 bg-gradient-to-br from-white/50 to-transparent group-hover:from-[#D4AF37]/20 group-hover:to-[#0056D2]/10 transition-colors duration-500">
                  {IconComponent && <IconComponent className="w-8 h-8 text-[#0056D2] group-hover:text-[#D4AF37] transition-colors duration-300" strokeWidth={1.5} />}
                </div>
                <h4 className="font-bold text-sm text-slate-900 mb-2">{item.title}</h4>
                <p className="text-xs text-slate-600 font-medium">{item.desc}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section ref={ref} className="py-20 relative overflow-hidden" data-testid="stats-section">
      <div className="absolute inset-0 bg-gradient-to-r from-[#D4AF37] via-[#F3E5AB] to-[#D4AF37] bg-[length:200%_auto] animate-[shimmer_5s_linear_infinite]" />
      <div className="absolute inset-0 noise-overlay opacity-50" />
      
      <div className="max-w-[1400px] mx-auto px-6 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: index * 0.1, type: "spring", stiffness: 100 }}
              whileHover={{ scale: 1.1 }}
              className="text-center p-6 rounded-2xl bg-white/20 backdrop-blur-sm border border-white/40 shadow-xl"
              data-testid={`stat-${index}`}
            >
              <span className="counter-value text-5xl md:text-6xl font-bold text-slate-900 block mb-2 drop-shadow-md">
                <Counter value={stat.value} suffix={stat.suffix} />
              </span>
              <span className="text-slate-800 text-sm uppercase tracking-wider font-bold drop-shadow-sm">
                {stat.label}
              </span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Projects Section
const ProjectsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-24 md:py-32 bg-slate-900 relative overflow-hidden" data-testid="projects-section">
      {/* Background elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-br from-[#0056D2]/10 to-transparent blur-[100px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-[#D4AF37]/10 to-transparent blur-[100px] rounded-full pointer-events-none" />
      
      <div className="max-w-[1400px] mx-auto px-6 relative z-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="section-label text-[#D4AF37] mb-6 block font-bold">Our Portfolio</span>
            <h2 className="text-4xl md:text-5xl font-bold text-white drop-shadow-lg">
              Featured Projects
            </h2>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="inline-block">
              <Link to="/projects/gallery" className="btn-modern btn-outline-gold premium-glass-dark border border-[#D4AF37]/50 shadow-2xl hover:shadow-[#D4AF37]/20">
                View All Projects <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </motion.div>
        </div>

        <motion.div 
          initial="hidden"
          animate={isInView ? "show" : "hidden"}
          variants={{
            hidden: { opacity: 0 },
            show: { opacity: 1, transition: { staggerChildren: 0.1 } }
          }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {PROJECTS.slice(0, 4).map((project, index) => (
            <motion.div
              key={project.id}
              variants={{
                hidden: { opacity: 0, y: 50 },
                show: { opacity: 1, y: 0, transition: { type: "spring", stiffness: 100 } }
              }}
              whileHover={{ y: -15 }}
              className="project-card-modern aspect-[3/4] rounded-xl overflow-hidden shadow-2xl border border-white/10 group"
              data-testid={`project-${project.id}`}
            >
              <img 
                src={project.image} 
                alt={project.title} 
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="project-overlay bg-gradient-to-t from-slate-900 via-slate-900/60 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-500" />
              <div className="project-content transform translate-y-8 group-hover:translate-y-0 transition-all duration-500">
                <span className="text-[#D4AF37] text-xs uppercase tracking-wider font-bold drop-shadow-sm">
                  {project.category}
                </span>
                <h3 className="text-xl font-bold text-white mt-2 drop-shadow-md">{project.title}</h3>
                <p className="text-white/80 text-sm mt-1 font-medium">{project.location}</p>
                <div className="mt-4 pt-4 border-t border-white/20 text-[#D4AF37] text-sm font-bold flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-500 delay-100">
                  View Case Study <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "", phone: "", email: "", service: "", message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setSubmitStatus("success");
        setFormData({ name: "", phone: "", email: "", service: "", message: "" });
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      setSubmitStatus("error");
    }
    setIsSubmitting(false);
    setTimeout(() => setSubmitStatus(null), 8000);
  };

  return (
    <section className="py-24 md:py-32 bg-transparent relative overflow-hidden" data-testid="contact-section">
      {/* Background glow */}
      <motion.div 
        animate={{ scale: [1, 1.1, 1], opacity: [0.2, 0.4, 0.2] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-[#D4AF37]/10 to-transparent blur-[120px] rounded-full pointer-events-none"
      />

      <div className="max-w-[1400px] mx-auto px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Info */}
          <div>
            <span className="section-label mb-6 block font-bold">Get in Touch</span>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 drop-shadow-sm">
              Let's Discuss Your Project
            </h2>
            <div className="gradient-line mb-8" />
            
            <p className="text-lg text-slate-700 font-medium mb-12">
              Contact us for a free consultation and quote. Our team of experts is ready to help 
              bring your vision to life.
            </p>

            <div className="space-y-6">
              {[
                { icon: Phone, label: "Call Us", value: CONTACT_INFO.phones.join(" / "), href: `tel:${CONTACT_INFO.phones[0]}` },
                { icon: Mail, label: "Email Us", value: CONTACT_INFO.email, href: `mailto:${CONTACT_INFO.email}` },
                { icon: MapPin, label: "Visit Us", value: CONTACT_INFO.address },
                { icon: Clock, label: "Working Hours", value: CONTACT_INFO.workingHours }
              ].map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  {item.href ? (
                    <motion.a whileHover={{ x: 10 }} href={item.href} className="flex items-center gap-4 p-4 premium-glass hover:border-[#D4AF37]/50 shadow-lg transition-all duration-300 group rounded-xl">
                      <div className="icon-container bg-[#0056D2]/5 group-hover:bg-[#D4AF37]/10 transition-colors">
                        <item.icon className="w-6 h-6 text-[#0056D2] group-hover:text-[#D4AF37] transition-colors" />
                      </div>
                      <div>
                        <span className="text-xs text-slate-500 font-bold uppercase tracking-wider">{item.label}</span>
                        <p className="text-slate-900 font-bold group-hover:text-[#D4AF37] transition-colors">{item.value}</p>
                      </div>
                    </motion.a>
                  ) : (
                    <motion.div whileHover={{ x: 10 }} className="flex items-center gap-4 p-4 premium-glass shadow-lg rounded-xl">
                      <div className="icon-container bg-[#0056D2]/5">
                        <item.icon className="w-6 h-6 text-[#0056D2]" />
                      </div>
                      <div>
                        <span className="text-xs text-slate-500 font-bold uppercase tracking-wider">{item.label}</span>
                        <p className="text-slate-900 font-bold">{item.value}</p>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>

          {/* Form */}
          <motion.div 
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, type: "spring", stiffness: 80 }}
            className="blueprint-form animated-gradient-border shadow-2xl rounded-2xl"
          >
            <h3 className="text-2xl font-bold text-slate-900 mb-8">Send Inquiry</h3>
            
            {submitStatus === "success" && (
              <div className="mb-6 p-4 bg-green-50 border-l-4 border-green-500 text-green-700 flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5" />
                Thank you! We'll get back to you soon.
              </div>
            )}

            {submitStatus === "error" && (
              <div role="alert" className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
                Something went wrong. Please try again or contact us directly.
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <input
                type="text"
                placeholder="Full Name"
                data-testid="form-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="input-modern"
              />
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <input
                  type="tel"
                  placeholder="Phone Number"
                  data-testid="form-phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="input-modern"
                />
                <input
                  type="email"
                  placeholder="Email Address"
                  data-testid="form-email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="input-modern"
                />
              </div>

              <select
                value={formData.service}
                data-testid="form-service"
                onChange={(e) => setFormData({ ...formData, service: e.target.value })}
                required
                className="input-modern cursor-pointer"
              >
                <option value="">Select a Service</option>
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.title}>{s.title}</option>
                ))}
              </select>

              <textarea
                placeholder="Project Details"
                data-testid="form-message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                rows={4}
                required
                className="input-modern resize-none"
              />

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                data-testid="form-submit"
                disabled={isSubmitting}
                className="btn-modern btn-gold w-full disabled:opacity-50 shadow-xl"
              >
                {isSubmitting ? "Sending..." : <>Send Inquiry <Send className="w-5 h-5" /></>}
              </motion.button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Main Home Page
const HomePage = () => {
  return (
    <main>
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <WhyChooseSection />
      <StatsSection />
      <ProjectsSection />
      <ContactSection />
    </main>
  );
};

export default HomePage;
